import _ from 'underscore';
import Backbone from "backbone";
import Sequencer from "models/audioEditor/Sequencer";
import SequencerNodeUtils from 'models/audioEditor/SequencerNodeUtils';
import SequencerRendererHelper from "models/audioEditor/SequencerRendererHelper";
import RSVP from "rsvp";

const SequencerRendererUtils = Backbone.Model.extend({
    idAttribute: "id",

    getRenderedAudioBlob: function (projectId, renderWavFile, asVideo, progressReportFunction, alreadyRenderedAudioWAVBlob, skipAudioProcessing)
    {
        return this.withSequencer(projectId, function (sequencer) {
            return sequencer.getRenderedAudioBlob(renderWavFile, asVideo, progressReportFunction, alreadyRenderedAudioWAVBlob, skipAudioProcessing);
        }, progressReportFunction);
    },

    getSrtText: function (projectId, asVtt, doNotIncludeSpeaker, progressReportFunction)
    {
        return this.withSequencer(projectId, function (sequencer) {
            return sequencer.getSrtText(null, null, progressReportFunction, asVtt, doNotIncludeSpeaker);
        }, progressReportFunction);
    },

    getTranscriptionTextFile: async function (projectId, includeSpeaker, progressReportFunction)
    {
        return this.withSequencer(projectId, async function (sequencer) {
            return sequencer.getSelectedNodesTextContent(null, null, includeSpeaker);
        }, progressReportFunction);
    },

    _createSequencerWithProjectId: async function (projectId, progressReportFunction)
    {
        const sequencer = new Sequencer();
        await sequencer.setAndLoadProject(projectId, false, progressReportFunction)
        return sequencer;

    },

    /**
     * Create a sequencer with proper cleanup capabilities
     * @param {string} projectId - The project ID to load
     * @param {Function} progressReportFunction - Progress callback
     * @returns {Promise<{sequencer: Sequencer, cleanup: Function}>} - Sequencer with cleanup function
     */
    createSequencerWithCleanup: async function(projectId, progressReportFunction) {
        const sequencer = await this._createSequencerWithProjectId(projectId, progressReportFunction);
        
        const cleanup = async () => {
            try {
                try {
                    // Stop any active playback
                    if (sequencer.stopPlaybackIfApplicable) {
                        await sequencer.stopPlaybackIfApplicable();
                    }

                    // Clear any pending operations or timeouts
                    if (sequencer._clearAnalysisState) {
                        sequencer._clearAnalysisState();
                    }

                    // Clear search and other cached data
                    if (sequencer.clearSearch) {
                        sequencer.clearSearch();
                    }
                } finally {
                    SequencerRendererHelper.getInstance().releaseSequencer(sequencer);
                }
                
                // Additional cleanup can be added here as needed
                // (audio buffer cleanup, WebAudio node disconnection, etc.)
                
            } catch (error) {
                console.warn('Error during sequencer cleanup:', error);
            }
        };
        
        return { sequencer, cleanup };
    },

    /**
     * Execute a function with a sequencer, ensuring proper cleanup
     * @param {string} projectId - The project ID to load
     * @param {Function} fn - Function to execute with the sequencer
     * @param {Function} progressReportFunction - Progress callback
     * @returns {Promise<*>} - Result of the function execution
     */
    withSequencer: async function(projectId, fn, progressReportFunction) {
        const { sequencer, cleanup } = await this.createSequencerWithCleanup(projectId, progressReportFunction);
        
        try {
            return await fn(sequencer);
        } finally {
            await cleanup();
        }
    },

    playWithSelectionButtonClick: (sequencer, startSequencerNodeModel, endSequencerNodeModel, playBySentences, includeDeletedSegmentsWhenPossible, numberOfWordsBeforeAndAfterToPlay = 4) =>
    {

        const firstSequencerNodeToPlay = playBySentences? SequencerNodeUtils.getInstance().getPreviousWordSequencerNodeAtBeginningOfSentence(startSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true): SequencerNodeUtils.getInstance().getPreviousNWordSequencerNode(startSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true);
        const lastSequencerNodeToPlay = playBySentences? SequencerNodeUtils.getInstance().getNextWordSequencerNodeAtEndOfSentence(endSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true):SequencerNodeUtils.getInstance().getNextNWordSequencerNode(endSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true);
        sequencer.playPauseSequencerRenderingFromNode(firstSequencerNodeToPlay, true, 0, lastSequencerNodeToPlay, null, includeDeletedSegmentsWhenPossible);

    },

    playWithoutSelectionButtonClick: (sequencer, startSequencerNodeModel, endSequencerNodeModel, playBySentences, numberOfWordsBeforeAndAfterToPlay = 4) =>
    {
        const arrayOfSequencerNodesToIgnore = SequencerNodeUtils.getInstance().getArrayOfSequencerNodesFromStartToEnd(startSequencerNodeModel, endSequencerNodeModel);
        if(arrayOfSequencerNodesToIgnore.length > 40){
            playBySentences = true;
        }
        const firstSequencerNodeToPlay = playBySentences? SequencerNodeUtils.getInstance().getPreviousWordSequencerNodeAtBeginningOfSentence(startSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true): SequencerNodeUtils.getInstance().getPreviousNWordSequencerNode(startSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true);
        const lastSequencerNodeToPlay = playBySentences? SequencerNodeUtils.getInstance().getNextWordSequencerNodeAtEndOfSentence(endSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true): SequencerNodeUtils.getInstance().getNextNWordSequencerNode(endSequencerNodeModel, numberOfWordsBeforeAndAfterToPlay, true);
        const shouldSequencerNodeBePlayedValidatorFunction = (sequencerNode, nodeSchedulingInfo) =>
        {
            if (arrayOfSequencerNodesToIgnore)
            {
                return !arrayOfSequencerNodesToIgnore.includes(sequencerNode);
            }
            return true;
        }
        sequencer.playPauseSequencerRenderingFromNode(firstSequencerNodeToPlay, true, 0, lastSequencerNodeToPlay, shouldSequencerNodeBePlayedValidatorFunction);
    }



});



const SequencerRendererUtilsInstance = new SequencerRendererUtils();
window.trebble.globalHelpers.SequencerRendererUtils = SequencerRendererUtilsInstance;

export default {
    getInstance: function ()
    {
        return SequencerRendererUtilsInstance;
    }
};
